import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Coffee, Gift } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BirthdayBox } from "@/components/customer/BirthdayBox";

export function StampCard({ user, onUserUpdate }) {
  const { t } = useLanguage();
  const [claiming, setClaiming] = useState(false);
  const [goal, setGoal] = useState(10);
  const [rewardName, setRewardName] = useState("");
  const stamps = user?.stamps || 0;

  useEffect(() => {
    api.get("/settings").then((r) => {
      setGoal(r.data.stamps_goal || 10);
      setRewardName(r.data.reward_name || "");
    }).catch(() => {});
  }, [user]);

  const claim = async () => {
    setClaiming(true);
    try {
      const r = await api.post("/loyalty/claim");
      onUserUpdate(r.data);
      toast.success(t("reward_claimed"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="max-w-lg" data-testid="stamp-card-container">
      <div className="relative overflow-hidden bg-gradient-to-br from-amber-900 via-amber-950 to-stone-900 text-amber-50 rounded-2xl p-6 shadow-xl border border-amber-500/20">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest font-semibold text-amber-200/60">{t("brand")}</p>
            <h3 className="font-heading text-2xl font-bold mt-1 notranslate" translate="no" data-testid="stamp-card-title">{t("stamps_title")}</h3>
          </div>
          <Coffee className="w-8 h-8 text-amber-400/70" />
        </div>
        <div className="grid grid-cols-5 gap-3 sm:gap-4 my-6">
          {Array.from({ length: goal }, (_, i) => (
            <motion.div
              key={i}
              initial={false}
              animate={i < stamps ? { scale: [0.6, 1.15, 1] } : {}}
              transition={{ duration: 0.35 }}
              data-testid={`stamp-${i}`}
              className={`aspect-square rounded-full flex items-center justify-center border-2 ${
                i < stamps
                  ? "bg-amber-500 border-amber-400 text-stone-900"
                  : "bg-transparent border-[#3B302B] text-[#3B302B]"
              }`}
            >
              <Coffee className="w-5 h-5" />
            </motion.div>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-semibold" data-testid="stamp-count">
              {stamps} {t("stamps_of")} {goal}
            </p>
            <p className="text-xs text-amber-200/60 mt-0.5">
              {t("rewards_count")}: {user?.rewards_claimed || 0}
            </p>
          </div>
          {stamps >= goal && (
            <Button
              data-testid="claim-reward-btn"
              onClick={claim}
              disabled={claiming}
              className="rounded-full bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold gap-1.5"
            >
              <Gift className="w-4 h-4" /> {t("claim_reward")}{rewardName ? `: ${rewardName}` : ""}
            </Button>
          )}
        </div>
      </div>
      <BirthdayBox user={user} onUserUpdate={onUserUpdate} />
      <p className="text-sm text-[#786C66] mt-4">{t("stamps_subtitle")}</p>
    </div>
  );
}
